import { memo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { CategoryRow, SectionRow } from "@/lib/categories.functions";
import { getCulinaryPageData, type MenuType } from "@/lib/culinary.functions";
import { useGalleryGestureLock } from "@/hooks/use-gallery-gesture-lock";
import rest1 from "@/assets/restaurant-1.jpg";
import rest2 from "@/assets/restaurant-2.jpg";
import rest3 from "@/assets/restaurant-3.jpg";
import rest4 from "@/assets/restaurant-4.jpg";

type Props = {
  category: CategoryRow;
  sections: SectionRow[];
  appendCta?: boolean;
  experienceSlug?: string | null;
  isActive?: boolean;
};

const MENU_META: Record<MenuType, { kicker: string; title: string }> = {
  breakfast: { kicker: "Morning", title: "Breakfast" },
  lunch: { kicker: "Midday", title: "Lunch" },
  dinner: { kicker: "Evening", title: "Dinner" },
};

const FALLBACK_GALLERY = [rest1, rest2, rest3, rest4, rest2, rest3, rest1, rest4];

function CulinaryCategoryPageImpl({ experienceSlug, isActive = true }: Props) {
  const [openMenu, setOpenMenu] = useState<MenuType | null>(null);
  useGalleryGestureLock(openMenu !== null);

  const fetchData = useServerFn(getCulinaryPageData);
  const { data } = useQuery({
    queryKey: ["culinary", "page", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchData({ data: { experience_slug: experienceSlug } })
        : fetchData(),
  });

  const content = data?.content ?? null;
  const heroImage = content?.hero_image_url || rest1;
  const heroKicker = content?.hero_kicker || "The Table";
  const heroTitle = content?.hero_title || "Culinary";
  const heroSubtitle =
    content?.hero_subtitle ||
    "Breakfast, lunch and dinner — thoughtful, seasonal, and rooted in the landscape that surrounds you.";
  const inclusionsSentence =
    content?.inclusions_sentence ||
    "Continental breakfast and nightly s'mores are included in your stay.\nLunch, dinner, snacks and curated beverages available for an additional cost.";

  const menusByType: Record<MenuType, string[]> = {
    breakfast: [],
    lunch: [],
    dinner: [],
  };
  for (const m of data?.menus ?? []) {
    menusByType[m.menu_type].push(m.image_url);
  }

  const gallery =
    data?.gallery && data.gallery.length > 0
      ? data.gallery.map((g) => g.image_url)
      : FALLBACK_GALLERY;

  return (
    <div className="relative w-full bg-background">
      {/* Hero */}
      <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
        <img
          src={heroImage}
          alt="Culinary at Backland"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          draggable={false}
        />
        <div className="mobile-hero-scrim" />
        <div className="mobile-hero-copy px-7">
          <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
            {heroKicker}
          </div>
          {isActive ? (
            <h1 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch]">
              {heroTitle}
            </h1>
          ) : (
            <div className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch]">
              {heroTitle}
            </div>
          )}
          <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Dining info */}
      <section className="px-6 sm:px-8 py-14 sm:py-20 max-w-3xl mx-auto">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-6 text-center">
          Dining
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-6 text-center">
          <div>
            <h3 className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-gold/90 mb-3">
              Always included
            </h3>
            <ul className="space-y-1.5 text-sm sm:text-base text-foreground/90 font-light leading-snug">
              <li>Continental Breakfast</li>
              <li>nightly s&apos;mores</li>
            </ul>
          </div>
          <div>
            <h3 className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-gold/90 mb-3">
              Available to add
            </h3>
            <ul className="space-y-1.5 text-sm sm:text-base text-foreground/90 font-light leading-snug">
              <li>Dinner</li>
              <li>Box lunches</li>
              <li>snacks</li>
              <li>curated beverages</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 pl-4 sm:pl-5 py-2 border-l border-gold/40">
          <p className="font-serif text-base sm:text-lg text-foreground/90">
            Stay and Dine Rate
          </p>
          <p className="text-xs sm:text-sm text-foreground/60 font-light mt-1">
            includes all meals for a more effortless stay
          </p>
        </div>
      </section>

      {/* Sample menus */}
      <section className="px-6 sm:px-8 pb-16 sm:pb-24 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
            A Taste
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl text-foreground">
            Sample menus
          </h3>
          <p className="mt-2 text-sm text-foreground/60 font-light">
            Tap to preview what's on the table.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {(["breakfast", "lunch", "dinner"] as const).map((m) => {
            const disabled = menusByType[m].length === 0;
            return (
              <button
                key={m}
                onClick={() => !disabled && setOpenMenu(m)}
                disabled={disabled}
                className="group relative flex flex-col items-center justify-center min-h-[4.5rem] sm:min-h-[5rem] px-2 sm:px-4 py-3 rounded-xl border border-foreground/10 bg-white/[0.04] hover:bg-white/10 hover:border-gold/40 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/50 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                <span className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-1">
                  {MENU_META[m].kicker}
                </span>
                <span className="font-serif text-[15px] sm:text-base md:text-lg text-foreground group-hover:text-gold capitalize">
                  {m}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Mosaic gallery */}
      <section className="gallery-zoom-lock px-3 sm:px-4 pb-20">
        <div className="text-center mb-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
            From the kitchen
          </div>
        </div>
        <div className="gallery-zoom-lock max-w-5xl mx-auto columns-2 md:columns-3 gap-2 sm:gap-3 [&>figure]:mb-2 sm:[&>figure]:mb-3 [&>figure]:break-inside-avoid">
          {gallery.map((src, i) => {
            const aspect =
              i % 5 === 0 ? "aspect-[3/4]"
              : i % 5 === 1 ? "aspect-square"
              : i % 5 === 2 ? "aspect-[4/5]"
              : i % 5 === 3 ? "aspect-[4/3]"
              : "aspect-[3/4]";
            return (
              <figure
                key={`${src}-${i}`}
                className={`relative overflow-hidden bg-muted ${aspect}`}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                  draggable={false}
                />
              </figure>
            );
          })}
        </div>
      </section>

      {/* Menu dialog */}
      <Dialog open={openMenu !== null} onOpenChange={(o) => !o && setOpenMenu(null)}>
        <DialogContent hideCloseButton className="gallery-zoom-lock max-w-lg bg-background border-foreground/15 max-h-[85svh] overflow-y-auto">
          {openMenu && (
            <>
              <button
                type="button"
                onClick={() => setOpenMenu(null)}
                aria-label="Close"
                className="sticky top-0 z-20 justify-self-end -mt-2 -mr-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/70 border border-white/25 text-foreground hover:bg-black/90 transition shadow-lg focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                  {MENU_META[openMenu].kicker} · Sample
                </div>
                <DialogTitle className="font-serif text-3xl text-foreground">
                  {MENU_META[openMenu].title}
                </DialogTitle>
                <DialogDescription className="text-foreground/60 font-light">
                  A glimpse of what our kitchen is serving this season. Menus change with the harvest.
                </DialogDescription>
              </DialogHeader>
              <div className="gallery-zoom-lock mt-4 space-y-4">
                {menusByType[openMenu].map((url, i) => (
                  <img
                    key={`${url}-${i}`}
                    src={url}
                    alt={`${MENU_META[openMenu].title} menu ${i + 1}`}
                    className="w-full rounded-md border border-foreground/10"
                    loading="lazy"
                    draggable={false}
                  />
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CulinaryCategoryPage = memo(CulinaryCategoryPageImpl);
export default CulinaryCategoryPage;
